import { PostgrestResponse } from "@supabase/supabase-js";
import { ISaldosResponsaveis } from "../../../types/tables/ISaldosResponsaveis";
import { BaseMultiTenantService } from "../base/BaseMultiTenantService";

/**
 * Serviço para gerenciamento de saldos de responsáveis com isolamento multi-tenant
 * Gerencia créditos disponíveis para responsáveis
 */
export class SaldosResponsaveisService extends BaseMultiTenantService<ISaldosResponsaveis> {
  constructor() {
    super("saldos_responsaveis", [], "fk_id_organizacao");
  }

  /**
   * Busca saldo de um responsável específico
   */
  async getSaldoByResponsavel(
    organizationId: number,
    responsavelId: number,
  ): Promise<PostgrestResponse<ISaldosResponsaveis>> {
    return await this.client
      .from(this.tableName)
      .select("*")
      .eq(this.organizationIdField, organizationId)
      .eq("fk_id_responsavel", responsavelId)
      .single();
  }

  /**
   * Adiciona crédito ao saldo do responsável
   */
  async adicionarCredito(
    organizationId: number,
    responsavelId: number,
    valor: number,
    descricao: string,
    userId?: number,
  ): Promise<PostgrestResponse<ISaldosResponsaveis[]>> {
    // Buscar saldo atual
    const { data: saldoAtualRaw } = await this.getSaldoByResponsavel(
      organizationId,
      responsavelId,
    );
    const saldoAtual = saldoAtualRaw as any; // Conversão temporária

    let novoSaldo: number;
    let resultado: any;

    if (saldoAtual) {
      // Atualizar saldo existente
      novoSaldo = saldoAtual.saldo_atual + valor;
      resultado = await this.update(
        saldoAtual.id,
        {
          saldo_atual: novoSaldo,
          updated_at: new Date().toISOString(),
        },
        organizationId,
        userId,
      );
    } else {
      // Criar novo registro de saldo
      novoSaldo = valor;
      resultado = await this.create(
        {
          fk_id_responsavel: responsavelId,
          saldo_atual: novoSaldo,
          saldo_bloqueado: 0,
        },
        organizationId,
        userId,
      );
    }

    // Registrar movimentação
    await this.client.from("movimentacoes_saldo").insert({
      fk_id_pessoa: responsavelId,
      fk_id_organizacao: organizationId,
      tipo_movimentacao: "credito",
      tipo_origem: "deposito",
      valor: valor,
      saldo_anterior: saldoAtual?.saldo_atual || 0,
      saldo_posterior: novoSaldo,
      descricao: descricao,
      data_movimentacao: new Date().toISOString(),
      created_by: userId,
    });

    return resultado;
  }

  /**
   * Debita valor do saldo do responsável
   */
  async debitarSaldo(
    organizationId: number,
    responsavelId: number,
    valor: number,
    tipoOrigem: string,
    origemId: number,
    descricao: string,
    userId?: number,
  ): Promise<PostgrestResponse<ISaldosResponsaveis[]>> {
    const { data: saldoAtualRaw } = await this.getSaldoByResponsavel(
      organizationId,
      responsavelId,
    );
    const saldoAtual = saldoAtualRaw as any; // Conversão temporária

    if (!saldoAtual) {
      throw new Error("Responsável não possui saldo cadastrado");
    }

    if (saldoAtual.saldo_atual < valor) {
      throw new Error("Saldo insuficiente");
    }

    const novoSaldo = saldoAtual.saldo_atual - valor;

    // Atualizar saldo
    const resultado = await this.update(
      saldoAtual.id,
      {
        saldo_atual: novoSaldo,
        updated_at: new Date().toISOString(),
      },
      organizationId,
      userId,
    );

    // Registrar movimentação
    await this.client.from("movimentacoes_saldo").insert({
      fk_id_pessoa: responsavelId,
      fk_id_organizacao: organizationId,
      tipo_movimentacao: "debito",
      tipo_origem: tipoOrigem,
      origem_id: origemId,
      valor: valor,
      saldo_anterior: saldoAtual.saldo_atual,
      saldo_posterior: novoSaldo,
      descricao: descricao,
      data_movimentacao: new Date().toISOString(),
      created_by: userId,
    });

    return resultado;
  }

  /**
   * Transfere saldo do responsável para um aluno
   */
  async transferirParaAluno(
    organizationId: number,
    responsavelId: number,
    alunoId: number,
    valor: number,
    userId?: number,
  ): Promise<{ success: boolean; novoSaldoAluno: number }> {
    // Debitar do responsável
    await this.debitarSaldo(
      organizationId,
      responsavelId,
      valor,
      "transferencia",
      alunoId,
      `Transferência para aluno ID: ${alunoId}`,
      userId,
    );

    // Creditar no aluno - implementação direta para evitar dependência circular
    const { data: saldoAluno } = await this.client
      .from("saldos_alunos")
      .select("*")
      .eq("fk_id_organizacao", organizationId)
      .eq("fk_id_aluno", alunoId)
      .single();

    let novoSaldoAluno: number;

    if (saldoAluno) {
      // Atualizar saldo existente do aluno
      novoSaldoAluno = saldoAluno.saldo_disponivel + valor;
      await this.client
        .from("saldos_alunos")
        .update({
          saldo_disponivel: novoSaldoAluno,
          updated_at: new Date().toISOString(),
        })
        .eq("id", saldoAluno.id);
    } else {
      // Criar novo registro de saldo para o aluno
      novoSaldoAluno = valor;
      await this.client.from("saldos_alunos").insert({
        fk_id_aluno: alunoId,
        fk_id_responsavel: responsavelId,
        fk_id_organizacao: organizationId,
        saldo_disponivel: novoSaldoAluno,
        limite_diario: 0,
        limite_semanal: 0,
        created_at: new Date().toISOString(),
        created_by: userId,
      });
    }

    // Registrar movimentação para o aluno
    await this.client.from("movimentacoes_saldo").insert({
      fk_id_pessoa: alunoId,
      fk_id_organizacao: organizationId,
      tipo_movimentacao: "credito",
      tipo_origem: "transferencia",
      origem_id: responsavelId,
      valor: valor,
      saldo_anterior: saldoAluno?.saldo_disponivel || 0,
      saldo_posterior: novoSaldoAluno,
      descricao: `Transferência do responsável ID: ${responsavelId}`,
      data_movimentacao: new Date().toISOString(),
      created_by: userId,
    });

    return { success: true, novoSaldoAluno };
  }

  /**
   * Bloqueia valor no saldo (para transações pendentes)
   */
  async bloquearSaldo(
    organizationId: number,
    responsavelId: number,
    valor: number,
    userId?: number,
  ): Promise<PostgrestResponse<ISaldosResponsaveis[]>> {
    const { data: saldoAtualRaw } = await this.getSaldoByResponsavel(
      organizationId,
      responsavelId,
    );
    const saldoAtual = saldoAtualRaw as any; // Conversão temporária

    if (!saldoAtual) {
      throw new Error("Responsável não possui saldo cadastrado");
    }

    const saldoDisponivel = saldoAtual.saldo_atual - saldoAtual.saldo_bloqueado;

    if (saldoDisponivel < valor) {
      throw new Error("Saldo disponível insuficiente");
    }

    return await this.update(
      saldoAtual.id,
      {
        saldo_bloqueado: saldoAtual.saldo_bloqueado + valor,
        updated_at: new Date().toISOString(),
      },
      organizationId,
      userId,
    );
  }

  /**
   * Desbloqueia valor no saldo
   */
  async desbloquearSaldo(
    organizationId: number,
    responsavelId: number,
    valor: number,
    userId?: number,
  ): Promise<PostgrestResponse<ISaldosResponsaveis[]>> {
    const { data: saldoAtualRaw } = await this.getSaldoByResponsavel(
      organizationId,
      responsavelId,
    );
    const saldoAtual = saldoAtualRaw as any; // Conversão temporária

    if (!saldoAtual) {
      throw new Error("Responsável não possui saldo cadastrado");
    }

    return await this.update(
      saldoAtual.id,
      {
        saldo_bloqueado: Math.max(0, saldoAtual.saldo_bloqueado - valor),
        updated_at: new Date().toISOString(),
      },
      organizationId,
      userId,
    );
  }

  /**
   * Busca todos os saldos com informações dos responsáveis
   */
  async getAllWithResponsaveis(
    organizationId: number,
  ): Promise<PostgrestResponse<ISaldosResponsaveis[]>> {
    return await this.client
      .from(this.tableName)
      .select(
        `
                    *,
                    responsavel:fk_id_responsavel(id, nome, email)
               `,
      )
      .eq(this.organizationIdField, organizationId);
  }

  /**
   * Relatório de movimentações por responsável
   */
  async getRelatorioMovimentacoes(
    organizationId: number,
    responsavelId: number,
    dataInicio?: string,
    dataFim?: string,
  ): Promise<PostgrestResponse<ISaldosResponsaveis[]>> {
    let query = this.client
      .from("movimentacoes_saldo")
      .select("*")
      .eq("fk_id_organizacao", organizationId)
      .eq("fk_id_pessoa", responsavelId)
      .order("data_movimentacao", { ascending: false });

    if (dataInicio) {
      query = query.gte("data_movimentacao", dataInicio);
    }

    if (dataFim) {
      query = query.lte("data_movimentacao", dataFim);
    }

    return await query;
  }
}

export const saldosResponsaveisService = new SaldosResponsaveisService();
